import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import React, { useMemo, useState } from 'react';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { useLiveOrders } from './useLiveOrders';
import { KanbanBoard } from './KanbanBoard';
import { OrderDrawer } from './OrderDrawer';
import { tablet } from './theme';

/**
 * Live Orders — a read-only phase board for ops.
 *
 * Mirrors what the shop tablet shows, filtered to one branch (or all), with
 * every order clickable for detail. Deliberately has no Accept / Mark packed
 * controls: state transitions belong to the tablet, this is a window onto them.
 */
export default function LiveOrders() {
  const { byPhase, isLoading, orders, needsStore, isConnected } = useLiveOrders();

  // Hold the id, not the order. Holding the object froze the drawer on whatever
  // the order looked like when it was clicked, so a refetch behind an open
  // drawer left it showing a stale phase and an unfinished timeline.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useMemo(
    () => orders.find((o) => o.id === selectedId) ?? null,
    [orders, selectedId],
  );

  return (
    <Box sx={{ p: 3, bgcolor: tablet.bg, minHeight: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ fontSize: 24, fontWeight: 800, color: tablet.ink }}>Live Orders</Box>
          </Box>
          <Box sx={{ fontSize: 13, color: tablet.muted }}>
            {needsStore ? 'No store selected' : isLoading ? 'Loading…' : `${orders.length} order${orders.length === 1 ? '' : 's'} · view only`}
          </Box>
        </Box>

        {/* Branch filter lives in the app bar now — see StorePicker. */}

        {/* A dead socket and a quiet shop look identical on a board that never
            changes, so the connection state says which one you're looking at. */}
        {!needsStore && (
          <Tooltip
            title={isConnected
              ? 'Live — new orders appear as they are placed'
              : 'Not receiving live updates; refreshing on a timer instead'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
              <Box
                sx={{
                  width: 8, height: 8, borderRadius: '50%',
                  bgcolor: isConnected ? tablet.green : tablet.amber,
                }}
              />
              <Box sx={{ fontSize: 12, fontWeight: 700, color: isConnected ? tablet.muted : tablet.amber }}>
                {isConnected ? 'Live' : 'Reconnecting'}
              </Box>
            </Box>
          </Tooltip>
        )}
      </Box>

      {needsStore ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 10,
            color: tablet.muted,
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          Please select a store above
        </Box>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <KanbanBoard
          byPhase={byPhase}
          onCardClick={(o) => setSelectedId(o.id)}
          showStore
        />
      )}

      <OrderDrawer order={selected} onClose={() => setSelectedId(null)} />
    </Box>
  );
}
